'use client';

import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { Lootbox } from '@/api/common';
import { groupMemberVIP } from '@/helpers/user';
import { useGetCommonStore } from '@/store';
import {
  checkIsReachedDailyLootboxesLimit,
  getFirstIssuedLootbox,
} from '../components/ScratchGamePage/components/ScratchCard/helpers';
import {
  DAILY_LIMIT,
  EScratchTicketTheme,
  SCRATCH_GAME_CONFIG,
} from '../helpers';

export const useScratchGameLootbox = () => {
  const useCommonStore = useGetCommonStore();

  const {
    fetchLootboxes,
    scratchLootboxes: lootboxes,
    pendingLootboxes,
    isAuthenticated,
    scratchGameTheme,
    setScratchGameTheme,
    user,
    isFinished,
    setIsFinished,
    bonus,
    setBonus,
  } = useCommonStore(
    useShallow((state) => ({
      fetchLootboxes: state.fetchLootboxes,
      scratchLootboxes: state.scratchLootboxes,
      pendingLootboxes: state.pendingLootboxes,
      isAuthenticated: state.isAuthenticated,
      scratchGameTheme: state.scratchGameTheme,
      setScratchGameTheme: state.setScratchGameTheme,
      user: state.user,
      isFinished: state.isFinished,
      setIsFinished: state.setIsFinished,
      bonus: state.bonus,
      setBonus: state.setBonus,
    }))
  );

  const isVip = useMemo(() => groupMemberVIP(user), [user]);

  const currentConfig = isVip
    ? SCRATCH_GAME_CONFIG.vip
    : SCRATCH_GAME_CONFIG.default;

  const activeLootboxes = useMemo(
    () =>
      (Object.values(EScratchTicketTheme) as EScratchTicketTheme[]).reduce(
        (acc, theme) => {
          const filteredLootboxes = lootboxes.filter((l) =>
            l.title.includes(theme)
          );
          acc[theme] = getFirstIssuedLootbox(filteredLootboxes);
          return acc;
        },
        {} as Record<EScratchTicketTheme, Lootbox | undefined>
      ),
    [lootboxes]
  );

  const { isReachedDailyLimit, dailyActivatedLootboxes } = useMemo(
    () => checkIsReachedDailyLootboxesLimit(lootboxes),
    [lootboxes]
  );

  const dailyLimit = useMemo(
    () => (isAuthenticated ? dailyActivatedLootboxes : DAILY_LIMIT),
    [isAuthenticated, dailyActivatedLootboxes]
  );

  useEffect(() => {
    isAuthenticated && fetchLootboxes();
  }, [isAuthenticated, fetchLootboxes]);

  return {
    lootbox: activeLootboxes,
    isReachedDailyLimit: isReachedDailyLimit && isAuthenticated,
    pendingLootboxes,
    isAuthenticated,
    dailyActivatedLootboxes: dailyLimit,
    isFinished,
    setIsFinished,
    ticketTheme: scratchGameTheme,
    setTicketTheme: setScratchGameTheme,
    currentConfig,
    bonus,
    setBonus,
    isVip,
  };
};
